import { createConnect } from './connect';
import { Event } from './event';
import { env } from './env';
import { copyFile, assert } from './utils';
import { Request } from './request';

interface Config {
    // 应用ID， 在控制台应用详情查看
    appId: string;
    container?: string;
    onCloseModal?: () => Promise<void | boolean> | void | boolean;

    /**
     *  获取用户权益授权码
     */
    getUseRightCert?(info: {
        appId: string;
        abilityCode: string;
        workId: string;
    }): Promise<string | false | Error>;
    /**
     * 获取用户标识code
     */
    authorize?(): Promise<string>;
}

export function createKoutuEditor(config: Config) {
    const event = new Event();

    const iframeManager = createConnect({
        appId: config.appId,
        container: config.container,
        async onClose() {
            if (config.onCloseModal) {
                const res = await config.onCloseModal();
                if (res === false) return false;
            }
            event.emit('close');
        },
        methods: {
            async emit(name: string, ...args: any) {
                event.emit(name, ...args);
            },
            onload() {
                // 兼容
                event.emit('load');
            },
            async getUseRightToken(info: { appId: string; abilityCode: string; workId: string }) {
                if (!config.getUseRightCert) return false;
                const token = await config.getUseRightCert(info);
                if (token instanceof Error) {
                    return {
                        message: token.message,
                    };
                }
                return token;
            },
            async getUserCode() {
                if (!config.authorize) return false;
                try {
                    return await config.authorize();
                } catch (error) {
                    close();
                }
            },
            async download(info: any) {
                event.emit('save', info);
            },
        },
    });

    const tempCallbacks: Function[] = [];
    function cleanCallBack() {
        tempCallbacks.forEach((fn) => fn());
        tempCallbacks.length = 0;
    }

    async function importImage(file: string | Blob, query?: { [k: string]: any }) {
        cleanCallBack();
        iframeManager.update(env.KOUTU_EDITOR_URL, {
            ...query,
            version: config.authorize ? 'KE002' : 'KE001',
        });

        iframeManager.insert();

        event.once('load', async () => {
            const e = await iframeManager.getService();
            e.init({
                appId: config.appId,
                v: env.VERSION,
                version: config.authorize ? 'KE002' : 'KE001',
            });
            const data = typeof file === 'string' ? file : await copyFile(file);
            const service = await iframeManager.getService();
            service.importFile(data);
        });

        return new Promise<{ files: Blob[]; workId: number } | false>((resolve) => {
            tempCallbacks.push(event.once('save', (v) => resolve(v)));
            tempCallbacks.push(event.once('close', () => resolve(false)));
        });
    }

    async function editKoutu(id: string, query?: { [k: string]: any }) {
        assert(Number.isInteger(+id), `无效的ID ${id}`);

        cleanCallBack();
        iframeManager.update(env.KOUTU_EDITOR_URL, {
            ...query,
            id,
            mode: 'user',
            version: config.authorize ? 'KE002' : 'KE001',
        });
        iframeManager.insert();
        event.once('load', async () => {
            const e = await iframeManager.getService();
            e.init({
                appId: config.appId,
                v: env.VERSION,
                version: config.authorize ? 'KE002' : 'KE001',
            });
            e.openWork(id);
        });

        return new Promise<{ files: Blob[]; workId: number } | false>((resolve) => {
            tempCallbacks.push(event.once('save', (v) => resolve(v)));
            tempCallbacks.push(event.once('close', () => resolve(false)));
        });
    }

    async function koutu(url: string) {
        assert(typeof url === 'string', 'url 必须是 url 格式');
        if (!config.authorize || !config.getUseRightCert)
            throw new Error('缺少 authorize 或者 getUseRightCert 参数');
        const request = new Request();
        const code = await config.authorize();
        const token = await request.createAuth(code);
        const res = await request.createKoutuRecord({ token, url });
        const useCert = await config.getUseRightCert({
            abilityCode: 'KE002',
            appId: config.appId,
            workId: res,
        });
        if (!useCert) {
            throw new Error('没有权限');
        } else if (useCert instanceof Error) {
            throw useCert;
        }
        const data = await request.koutu({
            url,
            appId: config.appId,
            workId: res,
            abilityCode: 'KE002',
            useCert,
            token,
        });
        return { result: data, workId: res };
    }

    function onSave(fn: (info: { workId: string; files: Blob[] }) => void) {
        event.add('save', fn);
    }

    function onClose(fn: () => any) {
        event.add('close', fn);
    }

    function close() {
        event.emit('close');
        iframeManager.remove();
    }

    return {
        onSave,
        onClose,
        close,
        koutu,
        editKoutu,
        importImage,
    };
}
